import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { IndexedStorageService } from './indexed-storage.service';
import { StorageErrorHandlerService } from './storage-error-handler.service';
import { DbService } from './db.service';
import { StorageTTLConfig } from '../config/storage-ttl-config';
import { firstValueFrom } from 'rxjs';
import { requestIdleOrTimeout } from '../utils/idle-helper';

export interface CachedSurvey<T> {
  data: T;
  lastModified: number;
}

/**
 * Options for storage operations
 */
export interface StoreOptions {
  /** Debounce delay in milliseconds. 0 = immediate, 200/300 = autosave */
  debounceMs?: number;
  /** Skip expensive hash generation for deduplication */
  skipDedupHash?: boolean;
  /** Priority shorthand: 'boot' implies debounceMs=0, skipDedupHash=true */
  priority?: 'boot' | 'autosave';
}

/**
 * Service to manage storage strategy and migrations
 * Handles fallbacks between IndexedDB and localStorage
 * @author AI Assistant
 * @date 2025-01-17
 */
@Injectable({
  providedIn: 'root'
})
export class StorageManagerService {
  private isInitialized = false;
  private storageStrategy: 'indexeddb' | 'localstorage' = 'indexeddb';
  
  // Default TTL using centralized configuration
  private readonly DEFAULT_TTL_MS = StorageTTLConfig.STORAGE_RECORDS_TTL;
  
  // Cache to prevent duplicate operations
  private pendingOperations = new Map<string, Promise<void>>();
  
  // Debounce timers for frequent operations
  private debounceTimers = new Map<string, any>();
  
  // Track last stored data to prevent duplicates
  private lastStoredData = new Map<string, string>();
  private readonly surveyCachePrefix = 'survey-cache-';
  
  // Telemetry counters
  private deferredWritesCount = 0;
  private failedWritesCount = 0;
  private compressionFallbackCount = 0;

  constructor(
    private localStorageService: LocalStorageService,
    private indexedStorageService: IndexedStorageService,
    private errorHandler: StorageErrorHandlerService,
    private dbService: DbService
  ) {
    this.initialize();
  }

  /**
   * Initialize the storage manager
   * Non-blocking: defers expensive cleanup to idle time
   */
  private async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Test storage capabilities
      const capabilities = await this.errorHandler.testStorageCapabilities();
      
      if (capabilities.indexedDB) {
        this.storageStrategy = 'indexeddb';
        // console.log('Using IndexedDB as primary storage');
      } else if (capabilities.localStorage) {
        this.storageStrategy = 'localstorage';
        console.warn('Falling back to localStorage');
      } else {
        throw new Error('No storage mechanism available');
      }

      // Defer cleanup to idle time to avoid blocking critical path
      requestIdleOrTimeout(() => this.cleanupExpiredDataSafe(), 2000);
      
      // Clean up orphaned test data during initialization
      this.errorHandler.cleanupOrphanedTestData();
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize storage manager:', error);
      this.errorHandler.handleStorageError(error, undefined, true);
      this.storageStrategy = 'localstorage'; // Ultimate fallback
    }
  }

  /**
   * Store survey data with appropriate error handling
   * @param surveyId - Survey identifier
   * @param data - Survey data to store
   * @param optsOrTtl - Storage options or legacy TTL number
   */
  async storeSurveyData(surveyId: string, data: any, optsOrTtl?: number | StoreOptions): Promise<void> {
    // Handle legacy signature (ttl as number) and new signature (options object)
    let opts: StoreOptions = {};
    let ttlInMs: number | undefined;
    
    if (typeof optsOrTtl === 'number') {
      ttlInMs = optsOrTtl;
    } else if (optsOrTtl) {
      opts = optsOrTtl;
    }
    
    // Apply priority shorthand
    if (opts.priority === 'boot') {
      opts.debounceMs = 0;
      opts.skipDedupHash = true;
    } else if (opts.priority === 'autosave') {
      opts.debounceMs = opts.debounceMs ?? 200;
    }
    
    const debounceMs = opts.debounceMs ?? 200;
    const skipDedupHash = opts.skipDedupHash ?? false;
    const key = `construct-${surveyId}`;
    const finalTtl = ttlInMs ?? StorageTTLConfig.SURVEY_CONSTRUCT_TTL;
    
    // Immediate path (boot/critical)
    if (debounceMs === 0) {
      return this.storeNow(key, data, finalTtl, { skipDedupHash });
    }
    
    // Debounced path (autosave)
    return this.storeDebounced(key, data, debounceMs, finalTtl, { skipDedupHash });
  }

  /**
   * Store data immediately without debouncing (for critical boot path)
   * @private
   */
  private async storeNow(key: string, data: any, ttl: number, cfg: { skipDedupHash: boolean }): Promise<void> {
    // Check if operation is already in progress for this key
    if (this.pendingOperations.has(key)) {
      return this.pendingOperations.get(key)!;
    }
    
    const operationPromise = (async () => {
      try {
        // Skip expensive hash if requested (critical boot path)
        if (!cfg.skipDedupHash) {
          const dataHash = this.generateDataHash(data);
          if (this.lastStoredData.get(key) === dataHash) {
            return; // Skip duplicate
          }
          this.lastStoredData.set(key, dataHash);
        }
        
        await this.performStorageOperation(key, data, ttl, key.replace('construct-', ''));
        this.deferredWritesCount++;
      } catch (error) {
        this.failedWritesCount++;
        console.error(`[storeNow] Failed for ${key}:`, error);
        throw error;
      } finally {
        this.pendingOperations.delete(key);
      }
    })();
    
    this.pendingOperations.set(key, operationPromise);
    return operationPromise;
  }

  /**
   * Store data with debouncing (for autosave/frequent updates)
   * @private
   */
  private async storeDebounced(
    key: string,
    data: any,
    debounceMs: number,
    ttl: number,
    cfg: { skipDedupHash: boolean }
  ): Promise<void> {
    // Generate hash to check for duplicate data (unless skipped)
    if (!cfg.skipDedupHash) {
      const dataHash = this.generateDataHash(data);
      if (this.lastStoredData.get(key) === dataHash) {
        // console.log(`Skipping duplicate storage operation for ${key.replace('construct-', '')} (identical data)`);
        return;
      }
    }
    
    // Check if operation is already in progress
    if (this.pendingOperations.has(key)) {
      // console.log(`Storage operation already in progress for ${key}, waiting...`);
      return await this.pendingOperations.get(key);
    }
    
    // Clear any existing debounce timer
    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key)!);
      this.debounceTimers.delete(key);
    }
    
    // Create promise for this operation with debounce
    const operationPromise = new Promise<void>((resolve, reject) => {
      const timer = setTimeout(async () => {
        try {
          await this.performStorageOperation(key, data, ttl, key.replace('construct-', ''));
          // Store hash after successful operation
          if (!cfg.skipDedupHash) {
            const dataHash = this.generateDataHash(data);
            this.lastStoredData.set(key, dataHash);
          }
          resolve();
        } catch (error) {
          this.failedWritesCount++;
          reject(error);
        } finally {
          this.debounceTimers.delete(key);
          this.pendingOperations.delete(key);
        }
      }, debounceMs);
      
      this.debounceTimers.set(key, timer);
    });
    
    this.pendingOperations.set(key, operationPromise);
    return operationPromise;
  }

  /**
   * Perform the actual storage operation with retry logic
   */
  private async performStorageOperation(key: string, data: any, finalTtl: number, surveyId: string): Promise<void> {
    const maxRetries = 3;
    let lastError: Error | null = null;
    let operationCompleted = false;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await this.setItem(key, data, finalTtl);
        
        // Only log once per successful operation
        if (!operationCompleted) {
          // console.log(`Survey data stored successfully for ${surveyId} with TTL: ${finalTtl}ms (attempt ${attempt})`);
          operationCompleted = true;
        }
        return;
      } catch (error) {
        lastError = error;
        console.warn(`Storage attempt ${attempt} failed for ${surveyId}:`, error);
        
        // If it's a constraint error, wait a bit before retrying
        if (error.name === 'ConstraintError' && attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, attempt * 100));
          continue;
        }
        
        // For other errors, try alternative approaches
        break;
      }
    }

    // If all retries failed, try fallback approaches
    this.errorHandler.handleStorageError(lastError, key, true);
    
    try {
      // Try to compress the data if it's large
      const compressedData = this.compressLargeData(data);
      await this.setItem(key + '_compressed', compressedData, finalTtl);
      console.warn(`Survey data stored in compressed format for ${surveyId}`);
    } catch (compressionError) {
      console.error(`Compression fallback failed for ${surveyId}:`, compressionError);
      // Last resort: store in memory for session
      this.storeInMemory(key, data);
      this.compressionFallbackCount++;
      throw new Error(`Critical: Unable to persist survey data for ${surveyId}`);
    }
  }

  /**
   * Retrieve survey data with fallback mechanisms
   * @param surveyId - Survey identifier
   */
  async getSurveyData(surveyId: string): Promise<any> {
    const key = `construct-${surveyId}`;
    
    try {
      // Try main key first
      let data = await this.getItem(key);
      
      // Try compressed version if main not found
      if (!data) {
        const compressedData = await this.getItem(key + '_compressed');
        if (compressedData) {
          data = this.decompressData(compressedData);
        }
      }
      
      // Try memory storage as last resort
      if (!data) {
        data = this.getFromMemory(key);
      }
      
      return data;
    } catch (error) {
      this.errorHandler.handleStorageError(error, key, false);
      return null;
    }
  }

  async getSurveyCached<T>(cacheKey: string): Promise<CachedSurvey<T> | null> {
    return await this.getData<CachedSurvey<T>>(this.surveyCachePrefix + cacheKey);
  }

  async setSurveyCached<T>(cacheKey: string, value: CachedSurvey<T>): Promise<void> {
    const ttl = this.getTTLByType('cache');
    await this.storeData(this.surveyCachePrefix + cacheKey, value, ttl);
  }

  /**
   * Store form fields data
   * @param surveyId - Survey identifier
   * @param fields - Form fields data
   * @param optsOrTtl - Storage options or legacy TTL number
   */
  async storeFormFields(surveyId: string, fields: any, optsOrTtl?: number | StoreOptions): Promise<void> {
    // Handle legacy signature (ttl as number) and new signature (options object)
    let opts: StoreOptions = {};
    let ttlInMs: number | undefined;
    
    if (typeof optsOrTtl === 'number') {
      ttlInMs = optsOrTtl;
    } else if (optsOrTtl) {
      opts = optsOrTtl;
    }
    
    // Apply priority shorthand
    if (opts.priority === 'boot') {
      opts.debounceMs = 0;
      opts.skipDedupHash = true;
    } else if (opts.priority === 'autosave') {
      opts.debounceMs = opts.debounceMs ?? 300;
    }
    
    const debounceMs = opts.debounceMs ?? 300;
    const skipDedupHash = opts.skipDedupHash ?? false;
    const key = `form-fields-${surveyId}`;
    const finalTtl = ttlInMs ?? StorageTTLConfig.FORM_FIELDS_TTL;
    
    // Immediate path
    if (debounceMs === 0) {
      return this.storeNow(key, fields, finalTtl, { skipDedupHash });
    }
    
    // Debounced path
    // Check if operation is already in progress
    if (this.pendingOperations.has(key)) {
      return await this.pendingOperations.get(key);
    }
    
    // Debounce rapid form field updates
    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key)!);
    }
    
    const operationPromise = new Promise<void>((resolve, reject) => {
      const timer = setTimeout(async () => {
        this.debounceTimers.delete(key);
        try {
          await this.setItem(key, fields, finalTtl);
          resolve();
        } catch (error) {
          this.errorHandler.handleStorageError(error, key, true);
          this.failedWritesCount++;
          reject(error);
        } finally {
          this.pendingOperations.delete(key);
        }
      }, debounceMs);
      
      this.debounceTimers.set(key, timer);
    });
    
    this.pendingOperations.set(key, operationPromise);
    return operationPromise;
  }

  /**
   * Get form fields data
   * @param surveyId - Survey identifier
   */
  async getFormFields<T>(surveyId: string): Promise<T | null> {
    const key = `form-fields-${surveyId}`;
    
    try {
      return await this.getItem<T>(key);
    } catch (error) {
      this.errorHandler.handleStorageError(error, key, false);
      return null;
    }
  }

  /**
   * Generic set item with error handling
   * @param key - Storage key
   * @param value - Value to store
   * @param ttlInMs - Optional time-to-live (defaults to 1 month)
   */
  private async setItem(key: string, value: any, ttlInMs?: number): Promise<void> {
    const finalTtl = ttlInMs ?? this.DEFAULT_TTL_MS;
    
    if (this.storageStrategy === 'indexeddb') {
      await this.indexedStorageService.setItem(key, value, finalTtl);
    } else {
      await this.localStorageService.setAsync(key, value, finalTtl);
    }
  }

  /**
   * Generic get item with error handling
   */
  private async getItem<T>(key: string): Promise<T | null> {
    if (this.storageStrategy === 'indexeddb') {
      return await this.indexedStorageService.getItem<T>(key);
    } else {
      return await this.localStorageService.getAsync<T>(key);
    }
  }

  /**
   * Store any data with default TTL
   * @param key - Storage key
   * @param value - Value to store
   * @param ttlInMs - Optional time-to-live (defaults to 1 month)
   */
  async storeData(key: string, value: any, ttlInMs?: number): Promise<void> {
    const finalTtl = ttlInMs ?? this.DEFAULT_TTL_MS;
    
    try {
      await this.setItem(key, value, finalTtl);
    } catch (error) {
      this.errorHandler.handleStorageError(error, key, false);
      throw error;
    }
  }

  /**
   * Get any data by key
   * @param key - Storage key
   */
  async getData<T>(key: string): Promise<T | null> {
    try {
      return await this.getItem<T>(key);
    } catch (error) {
      this.errorHandler.handleStorageError(error, key, false);
      return null;
    }
  }

  /**
   * Remove item from storage
   */
  async removeItem(key: string): Promise<void> {
    try {
      if (this.storageStrategy === 'indexeddb') {
        await this.indexedStorageService.removeItem(key);
      } else {
        await this.localStorageService.removeAsync(key);
      }
    } catch (error) {
      this.errorHandler.handleStorageError(error, key, false);
    }
  }

  /**
   * Get the default TTL value
   */
  getDefaultTTL(): number {
    return this.DEFAULT_TTL_MS;
  }

  /**
   * Get TTL in human readable format
   */
  getDefaultTTLDescription(): string {
    return StorageTTLConfig.formatTTL(this.DEFAULT_TTL_MS);
  }

  /**
   * Get all TTL configurations for debugging/info
   */
  getAllTTLConfigurations(): {[key: string]: {ms: number, formatted: string}} {
    return StorageTTLConfig.getAllTTLConfigs();
  }

  /**
   * Get TTL by data type
   */
  getTTLByType(type: 'storage' | 'pending' | 'temporary' | 'form-fields' | 'survey-construct' | 'errors' | 'cache' | 'session' | 'persistent'): number {
    return StorageTTLConfig.getTTLByType(type);
  }

  /**
   * Clean up expired data and old survey constructs
   * Now processes in batches to avoid Long Tasks
   */
  async cleanupExpiredData(): Promise<void> {
    try {
      if (this.storageStrategy === 'indexeddb') {
        // Clean up expired storage records
        await this.indexedStorageService.deleteExpiredRecords();
        
        // Clean up expired pending answers
        const pendingCleanup = await this.dbService.cleanupExpiredPendingAnswers();
        
        // Clean up expired temporary answers
        const temporaryCleanup = await this.dbService.cleanupExpiredTemporaryAnswers();
        
        // Log total cleanup results
        const totalCleaned = pendingCleanup + temporaryCleanup;
        if (totalCleaned > 0) {
          // console.log(`Storage cleanup completed: ${pendingCleanup} pending answers, ${temporaryCleanup} temporary answers removed`);
        }
        
        // Clean up old survey constructs (older than 7 days) - in batches
        await this.cleanupOldSurveyConstructs();
      }
    } catch (error) {
      console.warn('Cleanup failed:', error);
    }
  }

  /**
   * Safe wrapper for cleanup that catches errors and yields control
   * @private
   */
  private async cleanupExpiredDataSafe(): Promise<void> {
    try {
      await this.cleanupExpiredData();
    } catch (error) {
      console.error('[cleanupExpiredDataSafe] Background cleanup failed:', error);
      // Report to telemetry if available
    }
  }

  /**
   * Clean up old survey constructs in batches to avoid blocking
   * @private
   */
  private async cleanupOldSurveyConstructs(): Promise<void> {
    const oldSurveys = await this.indexedStorageService.getItemsByPattern('construct-');
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const batchSize = 20; // Process 20 items at a time
    
    for (let i = 0; i < oldSurveys.length; i += batchSize) {
      const batch = oldSurveys.slice(i, i + batchSize);
      
      await Promise.all(batch.map(async (item) => {
        try {
          const data = JSON.parse(item.value);
          if (data.created_at && data.created_at < sevenDaysAgo) {
            await this.removeItem(item.key);
          }
        } catch (parseError) {
          // If we can't parse, it might be old format, remove it
          await this.removeItem(item.key);
        }
      }));
      
      // Yield control to main thread between batches
      if (i + batchSize < oldSurveys.length) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }

  /**
   * Flush all pending write operations (for beforeunload)
   * @returns Promise that resolves when all pending writes complete
   */
  async flushPendingWrites(): Promise<void> {
    const pending = Array.from(this.pendingOperations.values());
    if (pending.length === 0) return;
    
    // console.log(`[flushPendingWrites] Flushing ${pending.length} pending operations`);
    await Promise.all(pending.map(p => p.catch(err => err)));
  }

  /**
   * Get telemetry counters for monitoring
   */
  getTelemetryCounters(): { deferred: number; failed: number; compressionFallback: number } {
    return {
      deferred: this.deferredWritesCount,
      failed: this.failedWritesCount,
      compressionFallback: this.compressionFallbackCount
    };
  }

  /**
   * Get storage status and health
   */
  async getStorageHealth(): Promise<{
    strategy: string;
    available: boolean;
    errors: number;
    criticalErrors: number;
    recommendations: string[];
  }> {
    try {
      const errors = await firstValueFrom(this.errorHandler.errors$);
      const criticalErrors = errors.filter(e => e.critical);
      
      return {
        strategy: this.storageStrategy,
        available: this.isInitialized,
        errors: errors.length,
        criticalErrors: criticalErrors.length,
        recommendations: this.getHealthRecommendations(errors)
      };
    } catch (error) {
      console.warn('Failed to get storage health:', error);
      return {
        strategy: this.storageStrategy,
        available: this.isInitialized,
        errors: 0,
        criticalErrors: 0,
        recommendations: ['Error al obtener estado de salud del almacenamiento']
      };
    }
  }

  /**
   * Force migration from localStorage to IndexedDB
   */
  async forceMigration(): Promise<{success: boolean, migratedItems: number, errors: string[]}> {
    const result = {
      success: false,
      migratedItems: 0,
      errors: [] as string[]
    };

    try {
      if (this.storageStrategy !== 'indexeddb') {
        result.errors.push('IndexedDB not available for migration');
        return result;
      }

      // Get all localStorage items
      const localStorageItems: {[key: string]: any} = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          try {
            const value = localStorage.getItem(key);
            if (value) {
              localStorageItems[key] = value;
            }
          } catch (error) {
            result.errors.push(`Failed to read ${key}: ${error.message}`);
          }
        }
      }

      // Migrate to IndexedDB
      for (const [key, value] of Object.entries(localStorageItems)) {
        try {
          await this.indexedStorageService.setItem(key, value);
          result.migratedItems++;
        } catch (error) {
          result.errors.push(`Failed to migrate ${key}: ${error.message}`);
        }
      }

      result.success = result.errors.length === 0 || result.migratedItems > 0;
    } catch (error) {
      result.errors.push(`Migration failed: ${error.message}`);
    }

    return result;
  }

  // Memory storage as last resort (session only)
  private memoryStorage: {[key: string]: any} = {};

  private storeInMemory(key: string, value: any): void {
    this.memoryStorage[key] = {
      value,
      timestamp: Date.now()
    };
    console.warn(`Storing ${key} in memory as fallback`);
  }

  private getFromMemory(key: string): any {
    const item = this.memoryStorage[key];
    return item ? item.value : null;
  }

  /**
   * Compress large data objects
   */
  private compressLargeData(data: any): any {
    try {
      const jsonString = JSON.stringify(data);
      
      // Simple compression: remove whitespace and common patterns
      const compressed = jsonString
        .replace(/\s+/g, ' ')
        .replace(/,\s*}/g, '}')
        .replace(/,\s*]/g, ']')
        .replace(/{\s*/g, '{')
        .replace(/\[\s*/g, '[');
      
      return {
        _compressed: true,
        data: compressed,
        originalSize: jsonString.length,
        compressedSize: compressed.length
      };
    } catch (error) {
      console.warn('Compression failed:', error);
      return data;
    }
  }

  /**
   * Decompress data
   */
  private decompressData(compressedData: any): any {
    try {
      if (compressedData && compressedData._compressed) {
        return JSON.parse(compressedData.data);
      }
      return compressedData;
    } catch (error) {
      console.warn('Decompression failed:', error);
      return compressedData;
    }
  }

  /**
   * Get health recommendations based on errors
   */
  private getHealthRecommendations(errors: any[]): string[] {
    const recommendations: string[] = [];
    
    if (errors.length > 10) {
      recommendations.push('Alto número de errores de almacenamiento detectados');
    }
    
    const quotaErrors = errors.filter(e => e.type === 'quota_exceeded');
    if (quotaErrors.length > 0) {
      recommendations.push('Ejecutar limpieza de datos reguladamente');
    }
    
    const criticalErrors = errors.filter(e => e.critical);
    if (criticalErrors.length > 0) {
      recommendations.push('Resolver errores críticos inmediatamente');
    }
    
    return recommendations;
  }

  /**
   * Clear all pending operations and timers
   */
  clearPendingOperations(): void {
    // Clear all debounce timers
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer);
    }
    this.debounceTimers.clear();
    
    // Clear pending operations map
    this.pendingOperations.clear();
    
    // console.log('Cleared all pending storage operations');
  }

  /**
   * Get pending operations status
   */
  getPendingOperationsStatus(): {
    pendingCount: number;
    debounceCount: number;
    keys: string[];
  } {
    return {
      pendingCount: this.pendingOperations.size,
      debounceCount: this.debounceTimers.size,
      keys: Array.from(this.pendingOperations.keys())
    };
  }

  /**
   * Generate a simple hash for data to detect duplicates
   * @param data - Data to hash
   */
  private generateDataHash(data: any): string {
    try {
      const jsonString = JSON.stringify(data);
      // Simple hash function for duplicate detection
      let hash = 0;
      for (let i = 0; i < jsonString.length; i++) {
        const char = jsonString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return hash.toString();
    } catch (error) {
      // Fallback to timestamp if JSON.stringify fails
      return Date.now().toString();
    }
  }
}

